import { PasseriniAPI } from '@/services/apiService';

export type OtroTramiteCobroStatus = 'Cobrado' | 'Sin cobrar';
export type OtroTramiteType =
  | 'Certificación de firma'
  | 'Certificación de copia'
  | 'Poder general'
  | 'Poder especial'
  | 'Autorización menor'
  | 'Autorización conducir'
  | 'Contrato';

export interface CatalogOption {
  id: string;
  nombre: string;
  email?: string;
  roles?: string[];
  profile?: {
    dni?: string | null;
    telefono?: string | null;
    direccion?: string | null;
  };
  dni?: string;
  telefono?: string;
  userId?: string;
  source?: string;
}

export interface ResponsableOption extends CatalogOption {
  source?: 'responsable' | 'user';
  userId?: string;
}

export interface OtorganteOption extends CatalogOption {
  source?: 'otorgante' | 'user';
  userId?: string;
}

export interface GaranteFileRecord {
  fileName: string;
  fileSize: number;
  formattedSize: string;
  mimeType: string;
  fileUrl: string;
}

export interface GaranteRecord {
  id: string;
  nombre: string;
  dniFrente: GaranteFileRecord | null;
  dniDorso: GaranteFileRecord | null;
}

export interface GarantePayload {
  id?: string;
  nombre: string;
  dniFrente?: File | null;
  dniDorso?: File | null;
  removeFrente?: boolean;
  removeDorso?: boolean;
}

export type UserOption = ResponsableOption;

export interface OtroTramiteRecord {
  id: string;
  fecha: string;
  otorgantesCount: number;
  otorgantes: OtorganteOption[];
  responsablesCount: number;
  responsables: ResponsableOption[];
  assignedUsers: UserOption[];
  tipo: OtroTramiteType;
  detalle?: string | null;
  copiasActuales: number;
  copiasTotales: number;
  montoARS: number;
  montoUSD: number;
  cobroARS: OtroTramiteCobroStatus;
  cobroUSD: OtroTramiteCobroStatus;
  archivosCount?: number;
  facturasCount?: number;
  dniDocuments?: DniDocumentsResponse;
  garantesCount: number;
  garantes: GaranteRecord[];
}

export type OtroTramiteRetiroFilter = 'all' | 'retirados' | 'sin_retirar';
export type OtroTramiteCobroFilter = 'all' | 'cobrados' | 'sin_cobrar';
export type OtroTramiteTipoFilter = 'all' | OtroTramiteType;
export type OtroTramiteFacturaFilter = 'all' | 'con' | 'sin';
export type OtroTramiteSortField = 'id' | 'fecha' | 'otorgantes' | 'responsables' | 'garantes' | 'tipo';
export type OtroTramiteSortDirection = 'asc' | 'desc';

export interface ListOtrosTramitesParams {
  search?: string;
  retiro?: OtroTramiteRetiroFilter;
  cobro?: OtroTramiteCobroFilter;
  tipo?: OtroTramiteTipoFilter;
  factura?: OtroTramiteFacturaFilter;
  assignedUserId?: string;
  sort?: OtroTramiteSortField;
  order?: OtroTramiteSortDirection;
}

export interface ListOtrosTramitesResponse {
  data: OtroTramiteRecord[];
  total: number;
  summary: {
    total: number;
    pendientesPago: number;
  };
}

export interface CreateOtroTramitePayload {
  otorganteIds: string[]; // Can be otorgante IDs or user IDs (backend handles auto-copy)
  responsableIds: string[]; // Can be responsable IDs or user IDs (backend handles auto-copy)
  assignedUserIds?: string[];
  tipo: OtroTramiteType;
  detalle?: string;
  copiasTotales: number;
  copiasActuales?: number;
  montoARS: number;
  montoUSD: number;
  fecha?: string;
  cobroARS?: OtroTramiteCobroStatus;
  cobroUSD?: OtroTramiteCobroStatus;
  dniDocuments?: DniDocumentsPayload;
  garantes?: GarantePayload[];
}

export interface UpdateOtroTramitePayload extends Partial<CreateOtroTramitePayload> { }

export type DniDocumentoTipo = 'frente' | 'dorso';
export type DniPersonaTipo = 'otorgante' | 'responsable';

export interface DniDocumentoRecord {
  id: number;
  fileName: string;
  fileSize: number;
  formattedSize: string;
  mimeType: string;
  fileUrl: string;
  uploadedBy: {
    id: number;
    name: string;
    email: string;
  } | null;
  createdAt: string | null;
}

export type DniDocumentsResponse = {
  otorgante: Record<string, Partial<Record<DniDocumentoTipo, DniDocumentoRecord>>>;
  responsable: Record<string, Partial<Record<DniDocumentoTipo, DniDocumentoRecord>>>;
};

export interface DniDocumentoPayload {
  frente?: File | null;
  dorso?: File | null;
  removeFrente?: boolean;
  removeDorso?: boolean;
}

export type DniDocumentsPayload = {
  otorgante?: Record<string, DniDocumentoPayload>;
  responsable?: Record<string, DniDocumentoPayload>;
};

export interface SearchUsersParams {
  search?: string;
  paginate?: boolean;
  per_page?: number;
}

export interface SearchOtorgantesParams {
  search?: string;
  limit?: number;
}

export interface SearchResponsablesParams {
  search?: string;
  limit?: number;
}

export interface FetchDniDocumentsByPersonasParams {
  otorganteIds?: string[];
  responsableIds?: string[];
}

export interface CreateResponsablePayload {
  nombre: string;
  email: string;
  dni?: string;
  telefono?: string;
  userId?: string; // If copying from existing user
}

export interface CreateOtorgantePayload {
  nombre: string;
  email: string;
  dni?: string;
  telefono?: string;
  userId?: string; // If copying from existing user
}

export const otrosTramitesService = {
  buildTramiteFormData(payload: CreateOtroTramitePayload): FormData {
    const formData = new FormData();

    payload.otorganteIds.forEach((id, index) => {
      formData.append(`otorganteIds[${index}]`, id);
    });

    payload.responsableIds.forEach((id, index) => {
      formData.append(`responsableIds[${index}]`, id);
    });

    payload.assignedUserIds?.forEach((id, index) => {
      formData.append(`assignedUserIds[${index}]`, id);
    });

    formData.append('tipo', payload.tipo);

    if (typeof payload.detalle === 'string') {
      formData.append('detalle', payload.detalle);
    }

    formData.append('copiasTotales', String(payload.copiasTotales));

    if (typeof payload.copiasActuales === 'number') {
      formData.append('copiasActuales', String(payload.copiasActuales));
    }

    formData.append('montoARS', String(payload.montoARS ?? 0));
    formData.append('montoUSD', String(payload.montoUSD ?? 0));

    if (payload.fecha) {
      formData.append('fecha', payload.fecha);
    }

    this.appendDniDocumentsToFormData(formData, payload.dniDocuments);
    this.appendGarantesToFormData(formData, payload.garantes);
    return formData;
  },

  appendGarantesToFormData(formData: FormData, garantes?: GarantePayload[], isFullEdit = false): void {
    // Si es una edición completa, siempre enviamos el sentinel para que el backend
    // sepa que debe sincronizar garantes (incluso si el array es vacío = borrar todos)
    if (isFullEdit) {
      formData.append('garantes_provided', '1');
    }

    if (!garantes || garantes.length === 0) {
      return;
    }

    garantes.forEach((g, i) => {
      if (g.id) {
        formData.append(`garantes[${i}][id]`, g.id);
      }
      formData.append(`garantes[${i}][nombre]`, g.nombre);
      if (g.dniFrente) {
        formData.append(`garantes[${i}][dni_frente]`, g.dniFrente);
      }
      if (g.dniDorso) {
        formData.append(`garantes[${i}][dni_dorso]`, g.dniDorso);
      }
      if (g.removeFrente) {
        formData.append(`garantes[${i}][remove_frente]`, '1');
      }
      if (g.removeDorso) {
        formData.append(`garantes[${i}][remove_dorso]`, '1');
      }
    });
  },

  appendDniDocumentsToFormData(formData: FormData, dniDocuments?: DniDocumentsPayload): void {
    if (!dniDocuments) {
      return;
    }

    const personaTipos: DniPersonaTipo[] = ['otorgante', 'responsable'];
    personaTipos.forEach((personaTipo) => {
      const personas = dniDocuments[personaTipo];
      if (!personas) {
        return;
      }

      Object.entries(personas).forEach(([personaId, docs]) => {
        if (docs.frente) {
          formData.append(`dniDocuments[${personaTipo}][${personaId}][frente]`, docs.frente);
        }
        if (docs.dorso) {
          formData.append(`dniDocuments[${personaTipo}][${personaId}][dorso]`, docs.dorso);
        }
        if (docs.removeFrente) {
          formData.append(`dniDocuments[${personaTipo}][${personaId}][remove_frente]`, '1');
        }
        if (docs.removeDorso) {
          formData.append(`dniDocuments[${personaTipo}][${personaId}][remove_dorso]`, '1');
        }
      });
    });
  },

  /**
   * Listar otros trámites con filtros opcionales
   */
  async list(params: ListOtrosTramitesParams = {}): Promise<ListOtrosTramitesResponse> {
    const response = await PasseriniAPI.get('/otros-tramites', { params });
    return response.data;
  },

  /**
   * Obtener un trámite por ID
   */
  async getById(id: string): Promise<OtroTramiteRecord | null> {
    try {
      const response = await PasseriniAPI.get(`/otros-tramites/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Listar todos los otorgantes
   */
  async listOtorgantes(): Promise<OtorganteOption[]> {
    const response = await PasseriniAPI.get('/otorgantes');
    return response.data;
  },

  /**
   * Buscar otorgantes con filtros
   */
  async searchOtorgantes(params: SearchOtorgantesParams = {}): Promise<OtorganteOption[]> {
    const response = await PasseriniAPI.get('/otorgantes/search', { params });
    return response.data;
  },

  /**
   * Create a new otorgante (and optionally user)
   * If userId is provided, copies user data to otorgantes table
   * Otherwise creates new otorgante and user
   */
  async createOtorgante(payload: CreateOtorgantePayload): Promise<OtorganteOption> {
    const response = await PasseriniAPI.post('/otorgantes', payload);
    return response.data;
  },

  /**
   * Search responsables (merges responsables table + users table)
   * Backend automatically merges and prioritizes responsables
   */
  async searchResponsables(params: SearchResponsablesParams = {}): Promise<ResponsableOption[]> {
    const response = await PasseriniAPI.get('/responsables/search', { params });
    return response.data;
  },

  /**
   * Create a new responsable (and optionally user)
   * If userId is provided, copies user data to responsables table
   * Otherwise creates new responsable and user
   */
  async createResponsable(payload: CreateResponsablePayload): Promise<ResponsableOption> {
    const response = await PasseriniAPI.post('/responsables', payload);
    return response.data;
  },

  /**
   * Check if an email already exists in the system
   * Searches across otorgantes, responsables, and users tables
   * @returns true if email exists, false otherwise
   */
  async checkEmailExists(email: string): Promise<boolean> {
    const normalizedEmail = email.toLowerCase().trim();

    // Search in parallel across all sources
    const [otorgantes, responsables, users] = await Promise.all([
      this.searchOtorgantes({ search: normalizedEmail, limit: 50 }),
      this.searchResponsables({ search: normalizedEmail, limit: 50 }),
      this.searchUsers({ search: normalizedEmail, per_page: 50 }),
    ]);

    // Check for exact email match in any source
    const allResults = [...otorgantes, ...responsables, ...users];
    return allResults.some(
      (item) => item.email?.toLowerCase().trim() === normalizedEmail
    );
  },

  async getDniDocumentsByPersonas(
    params: FetchDniDocumentsByPersonasParams
  ): Promise<DniDocumentsResponse> {
    const response = await PasseriniAPI.get('/otros-tramites/dni-documents', {
      params: {
        otorganteIds: params.otorganteIds ?? [],
        responsableIds: params.responsableIds ?? [],
      },
    });

    return response.data;
  },

  /**
   * Search users with filters
   */
  async searchUsers(params: SearchUsersParams = {}): Promise<UserOption[]> {
    const response = await PasseriniAPI.get('/users', { params: { ...params, paginate: false } });
    return response.data.map((user: any) => ({
      id: String(user.id),
      nombre: user.name,
      email: user.email,
      roles: Array.isArray(user.roles)
        ? user.roles
          .map((role: any) => typeof role === 'string' ? role : role?.name)
          .filter((roleName: unknown): roleName is string => typeof roleName === 'string' && roleName.length > 0)
        : [],
      userId: String(user.id),
      source: 'user',
      profile: {
        dni: user.profile?.dni ?? null,
        telefono: user.profile?.telefono ?? null,
        direccion: user.profile?.direccion ?? null,
      },
      dni: user.profile?.dni ?? '',
      telefono: user.profile?.telefono ?? '',
    }));
  },

  /**
   * Create a quick user (for responsables)
   * @deprecated Use createResponsable instead
   */
  async createQuickUser(payload: { name: string; email: string; dni?: string; telefono?: string }): Promise<UserOption> {
    const response = await PasseriniAPI.post('/users/quick', payload);
    return {
      id: String(response.data.id),
      nombre: response.data.name,
      email: response.data.email,
      dni: response.data.dni,
      telefono: response.data.telefono,
    };
  },

  /**
   * Crear un nuevo trámite
   */
  async create(payload: CreateOtroTramitePayload): Promise<OtroTramiteRecord> {
    const formData = this.buildTramiteFormData(payload);
    const response = await PasseriniAPI.post('/otros-tramites', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Actualizar un trámite existente
   */
  async update(id: string, payload: UpdateOtroTramitePayload): Promise<OtroTramiteRecord> {
    const formData = new FormData();

    if (payload.otorganteIds) {
      payload.otorganteIds.forEach((otorganteId, index) => {
        formData.append(`otorganteIds[${index}]`, otorganteId);
      });
    }
    if (payload.responsableIds) {
      payload.responsableIds.forEach((responsableId, index) => {
        formData.append(`responsableIds[${index}]`, responsableId);
      });
    }
    if (payload.assignedUserIds) {
      payload.assignedUserIds.forEach((assignedUserId, index) => {
        formData.append(`assignedUserIds[${index}]`, assignedUserId);
      });
      if (payload.assignedUserIds.length === 0) {
        formData.append('clearAssignedUsers', '1');
      }
    }
    if (payload.tipo) {
      formData.append('tipo', payload.tipo);
    }
    if (typeof payload.detalle === 'string') {
      formData.append('detalle', payload.detalle);
    }
    if (typeof payload.copiasTotales === 'number') {
      formData.append('copiasTotales', String(payload.copiasTotales));
    }
    if (typeof payload.copiasActuales === 'number') {
      formData.append('copiasActuales', String(payload.copiasActuales));
    }
    if (typeof payload.montoARS === 'number') {
      formData.append('montoARS', String(payload.montoARS));
    }
    if (typeof payload.montoUSD === 'number') {
      formData.append('montoUSD', String(payload.montoUSD));
    }
    if (payload.fecha) {
      formData.append('fecha', payload.fecha);
    }

    this.appendDniDocumentsToFormData(formData, payload.dniDocuments);

    // Garantes: si el payload incluye el campo, es una edición completa (no quick-assign)
    if (payload.garantes !== undefined) {
      this.appendGarantesToFormData(formData, payload.garantes, true);
    }

    formData.append('_method', 'PUT');

    const response = await PasseriniAPI.post(`/otros-tramites/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Actualizar montos de un trámite
   */
  async updateMontos(id: string, montoARS: number, montoUSD: number): Promise<OtroTramiteRecord> {
    const response = await PasseriniAPI.patch(`/otros-tramites/${id}/montos`, {
      montoARS,
      montoUSD,
    });
    return response.data;
  },

  /**
   * Eliminar un trámite
   */
  async remove(id: string): Promise<void> {
    await PasseriniAPI.delete(`/otros-tramites/${id}`);
  },

  // ========== Métodos de Archivos ==========

  /**
   * Subir un archivo a un trámite
   */
  async uploadArchivo(
    otroTramiteId: string,
    file: File,
    tipo?: 'factura'
  ): Promise<import('../types').UploadArchivoResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (tipo) {
      formData.append('tipo', tipo);
    }

    const response = await PasseriniAPI.post(
      `/otros-tramites/${otroTramiteId}/archivos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Listar archivos de un trámite
   */
  async listArchivos(
    otroTramiteId: string,
    scope: 'all' | 'factura' | 'otros' = 'all'
  ): Promise<import('../types').ListArchivosResponse> {
    const response = await PasseriniAPI.get(`/otros-tramites/${otroTramiteId}/archivos`, {
      params: { scope },
    });
    return response.data;
  },

  /**
   * Eliminar un archivo
   */
  async deleteArchivo(archivoId: number): Promise<void> {
    await PasseriniAPI.delete(`/archivos/${archivoId}`);
  },

  /**
   * Descargar un archivo (trigger browser download)
   */
  async downloadArchivo(archivo: import('../types').ArchivoRecord): Promise<void> {
    try {
      const response = await PasseriniAPI.get(archivo.fileUrl?.replace("/api", ""), {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = archivo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading archivo:', error);
      throw error;
    }
  },
};

export type OtrosTramitesService = typeof otrosTramitesService;

export const otrosTramitesRetiroFilters: ReadonlyArray<{ value: OtroTramiteRetiroFilter; label: string }> = Object.freeze([
  { value: 'all', label: 'Todos' },
  { value: 'retirados', label: 'Retirados' },
  { value: 'sin_retirar', label: 'Sin retirar' },
]);

export const otrosTramitesCobroFilters: ReadonlyArray<{ value: OtroTramiteCobroFilter; label: string }> = Object.freeze([
  { value: 'all', label: 'Todos' },
  { value: 'cobrados', label: 'Cobrados' },
  { value: 'sin_cobrar', label: 'Sin cobrar' },
]);

export const otrosTramitesTipoFilters: ReadonlyArray<{ value: OtroTramiteTipoFilter; label: string }> = Object.freeze([
  { value: 'all', label: 'Todos los tipos' },
  { value: 'Certificación de firma', label: 'Certificación de firma' },
  { value: 'Certificación de copia', label: 'Certificación de copia' },
  { value: 'Poder general', label: 'Poder general' },
  { value: 'Poder especial', label: 'Poder especial' },
  { value: 'Autorización menor', label: 'Autorización menor' },
  { value: 'Autorización conducir', label: 'Autorización conducir' },
  { value: 'Contrato', label: 'Contrato' },
]);

export const otrosTramitesFacturaFilters: ReadonlyArray<{ value: OtroTramiteFacturaFilter; label: string }> = Object.freeze([
  { value: 'all', label: 'Todos' },
  { value: 'con', label: 'Con factura' },
  { value: 'sin', label: 'Sin factura' },
]);

export const createTestOtrosTramitesService = (): OtrosTramitesService => otrosTramitesService;

export default otrosTramitesService;
